import { Routes, Route, Navigate } from 'react-router-dom';
import { IUILayout } from '@components/layout/IUILayout';
import { HomePage } from '@pages/HomePage';
import { TabsHubPage, TabsWorkflowsPage } from '@pages/TabsHubPage';
import { AIHubPage } from '@pages/AIHubPage';
import { AppsHubPage } from '@pages/AppsHubPage';
import { ProductivityHubPage } from '@pages/ProductivityHubPage';
import { InsightsHubPage } from '@pages/InsightsHubPage';
import { SyncHubPage } from '@pages/SyncHubPage';
import { SettingsHubPage } from '@pages/SettingsHubPage';
import { AgenticOrchestrationCenter } from '@pages/AgenticOrchestrationCenter';
import { SkillLibraryPage } from '@pages/SkillLibraryPage';
import { AppGenerator } from '@pages/AppGenerator';
import { AppDetailPage } from '@pages/AppDetailPage';
import { ComparisonBuilder } from '@pages/ComparisonBuilder';
import { TimelineBuilder } from '@pages/TimelineBuilder';
import { ResearchSynthesis } from '@pages/ResearchSynthesis';
import { DataAggregationPage } from '@pages/DataAggregationPage';
import { LoginPage } from '@pages/LoginPage';
import { RegisterPage } from '@pages/RegisterPage';
import { ForgotPasswordPage } from '@pages/ForgotPasswordPage';
import { ResetPasswordPage } from '@pages/ResetPasswordPage';
import { AuthCallbackPage } from '@pages/AuthCallbackPage';
import { LandingPage } from '@pages/LandingPage';
import { PublicAppSharePage } from '@pages/PublicAppSharePage';
import { RoleGuard } from '@components/auth/RoleGuard';
import { AuthGuard } from '@components/auth/AuthGuard';
import AdminUsersPage from '@pages/AdminUsersPage';
import { HUB_REDIRECTS, redirectTarget } from '@/routes/hubRoutes';

/**
 * Top-level app router.
 *
 * Architecture:
 *  - 8 primary hub routes (`/home`, `/tabs`, `/ai`, `/apps`, `/productivity`,
 *    `/insights`, `/sync`, `/settings`) plus a few first-class sub-routes
 *    (full-page generators, the Agentic Orchestration Center, the Skill
 *    Library).
 *  - Legacy routes (e.g. `/dashboard`, `/monitor`, `/generate`, …) emit a
 *    single `<Navigate replace>` to the right hub + `?section=` so old
 *    bookmarks, deep links, and Cursor agents still resolve.
 *  - The full list of redirects lives in `frontend/src/routes/hubRoutes.ts`,
 *    the single source of truth for the IA.
 */
export default function App() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/auth/callback" element={<AuthCallbackPage />} />
      <Route path="/public/apps/:token" element={<PublicAppSharePage />} />
      <Route path="/" element={<LandingPage />} />

      <Route
        element={
          <AuthGuard>
            <IUILayout />
          </AuthGuard>
        }
      >
        {/* 8 hub routes */}
        <Route path="home" element={<HomePage />} />
        <Route path="tabs" element={<TabsHubPage />} />
        <Route path="tabs/workflows" element={<TabsWorkflowsPage />} />
        <Route path="ai" element={<AIHubPage />} />
        <Route path="apps" element={<AppsHubPage />} />
        <Route path="productivity" element={<ProductivityHubPage />} />
        <Route path="insights" element={<InsightsHubPage />} />
        <Route path="sync" element={<SyncHubPage />} />
        <Route path="settings" element={<SettingsHubPage />} />

        {/* Settings sub-routes (full-page, not part of the hub tab strip) */}
        <Route path="settings/agentic" element={<AgenticOrchestrationCenter />} />
        <Route path="settings/skills" element={<SkillLibraryPage />} />

        {/* Admin (still gated by role) */}
        <Route
          path="settings/admin/users"
          element={
            <RoleGuard allow={['admin']}>
              <AdminUsersPage />
            </RoleGuard>
          }
        />

        {/* Apps sub-routes — static paths before :id */}
        <Route path="apps/generate" element={<AppGenerator />} />
        <Route path="apps/compare" element={<ComparisonBuilder />} />
        <Route path="apps/timeline" element={<TimelineBuilder />} />
        <Route path="apps/synthesize" element={<ResearchSynthesis />} />
        <Route path="apps/data-aggregation" element={<DataAggregationPage />} />
        <Route path="apps/:id" element={<AppDetailPage />} />

        {/* Legacy redirects: 85+ old routes → new hubs */}
        {HUB_REDIRECTS.map((r) => (
          <Route key={r.from} path={r.from.replace(/^\//, '')} element={<Navigate to={redirectTarget(r)} replace />} />
        ))}
      </Route>

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
